import { z } from 'zod'

/**
 * The Timesheets > Reports summary metrics, computed server-side over the
 * report's filters with the shared definitions in `utils/time-metrics.ts`.
 *
 * Money fields are null — never 0 — when the viewer may not see them, so a
 * masked figure can't be mistaken for "nothing billed". Percentages are 0–100
 * and null when their denominator is zero (no hours, no capacity, no revenue).
 */

export const TimeReportPayBasisSchema = z.enum([
  'ALL_HOURS',
  'BILLABLE_HOURS',
  'RETAINER',
])

/** Which money the viewer may see; mirrors the report export's columns. */
export const TimeReportVisibilitySchema = z.object({
  /** Billable value and effective rate. */
  canViewBill: z.boolean(),
  /** Labor cost (and margin, together with bill). */
  canViewCost: z.boolean(),
  /** Pay amounts and pay bases. */
  canViewPay: z.boolean(),
})

export const TimeReportSummaryTotalsSchema = z.object({
  totalHours: z.number(),
  billableHours: z.number(),
  nonBillableHours: z.number(),
  /** Billable hours ÷ total hours. */
  billableSharePercent: z.number().nullable(),
  /** Σ stored billable amount, else bill rate × hours, over billable time. */
  billableValue: z.number().nullable(),
  /** Cost (else pay) rate × paid hours, plus retainer shares. */
  laborCost: z.number().nullable(),
  /** Pay under each person's pay basis, plus retainer amounts. */
  payAmount: z.number().nullable(),
  /** (Billable value − labor cost) ÷ billable value. */
  marginPercent: z.number().nullable(),
  /** Billable value ÷ all hours logged. */
  effectiveHourlyRate: z.number().nullable(),
})

export const TimeReportCategoryRowSchema = z.object({
  /** null = uncategorized time. */
  categoryId: z.string().nullable(),
  name: z.string(),
  color: z.string().nullable(),
  hours: z.number(),
  laborCost: z.number().nullable(),
})

export const TimeReportPersonRowSchema = z.object({
  userId: z.string(),
  name: z.string(),
  totalHours: z.number(),
  billableHours: z.number(),
  /** Working capacity over the date range; null without a bounded range. */
  capacityHours: z.number().nullable(),
  billableUtilizationPercent: z.number().nullable(),
  targetBillablePercent: z.number().nullable(),
  /** Points above (+) or below (−) the target. */
  targetGap: z.number().nullable(),
  /** null when the viewer may not see pay terms. */
  payBasis: TimeReportPayBasisSchema.nullable(),
})

export const TimeReportSummarySchema = z.object({
  visibility: TimeReportVisibilitySchema,
  totals: TimeReportSummaryTotalsSchema,
  /** Non-billable time by category, by hours; "Uncategorized" last. */
  nonBillableByCategory: z.array(TimeReportCategoryRowSchema),
  byPerson: z.array(TimeReportPersonRowSchema),
})

export type TimeReportPayBasis = z.infer<typeof TimeReportPayBasisSchema>
export type TimeReportVisibility = z.infer<typeof TimeReportVisibilitySchema>
export type TimeReportSummaryTotals = z.infer<
  typeof TimeReportSummaryTotalsSchema
>
export type TimeReportCategoryRow = z.infer<typeof TimeReportCategoryRowSchema>
export type TimeReportPersonRow = z.infer<typeof TimeReportPersonRowSchema>
export type TimeReportSummary = z.infer<typeof TimeReportSummarySchema>
